import type { CSSProperties, ReactNode, SyntheticEvent } from "react";
import { useStoredFileObjectUrl } from "./useStoredFileObjectUrl";

export interface StoredFileImageProps {
  url: string | null | undefined;
  alt: string;
  className?: string;
  style?: CSSProperties;
  draggable?: boolean;
  onLoad?: (e: SyntheticEvent<HTMLImageElement>) => void;
  onError?: (e: SyntheticEvent<HTMLImageElement>) => void;
  fallback?: ReactNode;
  "data-testid"?: string;
}

/** An <img> for a stored file, loaded through the signed-in /api/files route (story 25240). */
export function StoredFileImage({ url, alt, className, style, draggable, onLoad, onError, fallback, "data-testid": testId }: StoredFileImageProps) {
  const { src, status } = useStoredFileObjectUrl(url);
  if (status === "idle") return null;
  // Placeholders take the image's className/style, so they sit where the image would.
  if (status === "error") {
    return (
      <>
        {fallback ?? (
          <span
            role="img"
            aria-label={alt}
            className={`inline-flex items-center justify-center min-h-[48px] w-full rounded bg-primary-gray-light-50 text-xs text-primary-gray-light-500 ${className ?? ""}`}
            style={style}
            data-testid="stored-file-image-error"
          >
            Image unavailable
          </span>
        )}
      </>
    );
  }
  if (!src) {
    return (
      <span
        role="status"
        aria-busy="true"
        aria-label={alt}
        className={`inline-block min-h-[48px] w-full animate-pulse rounded bg-primary-gray-light-100 ${className ?? ""}`}
        style={style}
        data-testid="stored-file-image-loading"
      />
    );
  }
  return <img src={src} alt={alt} className={className} style={style} draggable={draggable} onLoad={onLoad} onError={onError} data-testid={testId} />;
}

export default StoredFileImage;
